import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '..';
import { AUTH_CONFIG } from '../../config/env';
import { ApiError, ApiErrorType } from '../../hooks/useApi';

// Mock user type - would be defined in types/user.ts in a real app
interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  role?: string;
}

// Auth state interface
interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: AuthState = {
  user: null,
  token: localStorage.getItem(AUTH_CONFIG.TOKEN_KEY),
  isAuthenticated: false,
  loading: false,
  error: null,
};

// Valid credentials for development/demo
const VALID_CREDENTIALS = [
  { email: 'demo@example.com', password: 'password123', name: 'Demo User', role: 'creator' },
  { email: 'admin@blinklink.com', password: 'admin123', name: 'Admin User', role: 'admin' },
  // Add more users as needed
];

// Async thunks for authentication
export const login = createAsyncThunk<
  { user: User; token: string },
  { email: string; password: string },
  { rejectValue: string }
>('auth/login', async ({ email, password }, { rejectWithValue }) => {
  try {
    // In a real app, this would be an API call
    // const response = await fetch('/api/login', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ email, password }),
    // });
    // if (!response.ok) {
    //   const error = await ApiError.fromResponse(response);
    //   throw error;
    // }
    // const data = await response.json();
    // return data;

    // Mock implementation with credential validation
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Check if credentials are valid
    const validUser = VALID_CREDENTIALS.find(
      user => user.email === email && user.password === password
    );
    
    if (!validUser) {
      return rejectWithValue('Invalid email or password');
    }

    const mockUser = {
      id: '123',
      name: validUser.name,
      email: validUser.email,
      avatar: '/images/default-avatar.png',
      role: validUser.role
    };

    const mockToken = 'mock-jwt-token-' + Date.now();
    
    // Store token in localStorage with expiry
    localStorage.setItem(AUTH_CONFIG.TOKEN_KEY, mockToken);
    const expiryTime = new Date().getTime() + (24 * 60 * 60 * 1000); // 24 hours
    localStorage.setItem(AUTH_CONFIG.TOKEN_EXPIRY_KEY, expiryTime.toString());
    
    return { user: mockUser, token: mockToken };
  } catch (error) {
    let message = 'An unknown error occurred';
    
    if (error instanceof ApiError) {
      message = error.message;
    } else if (error instanceof Error) {
      message = error.message;
    }
    
    return rejectWithValue(message);
  }
});

export const logout = createAsyncThunk('auth/logout', async () => {
  // In a real app, this might involve an API call to invalidate the token
  localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
  localStorage.removeItem(AUTH_CONFIG.TOKEN_EXPIRY_KEY);
  localStorage.removeItem(AUTH_CONFIG.REFRESH_TOKEN_KEY);
  return null;
});

export const getCurrentUser = createAsyncThunk<
  User,
  void,
  { rejectValue: string; state: RootState }
>('auth/getCurrentUser', async (_, { getState, rejectWithValue }) => {
  try {
    const { auth } = getState();
    
    // If no token, reject
    if (!auth.token) {
      return rejectWithValue('No authentication token found');
    }
    
    // Check token expiry
    const tokenExpiry = localStorage.getItem(AUTH_CONFIG.TOKEN_EXPIRY_KEY);
    if (tokenExpiry && parseInt(tokenExpiry, 10) < new Date().getTime()) {
      // Token expired, try to refresh or logout
      localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
      localStorage.removeItem(AUTH_CONFIG.TOKEN_EXPIRY_KEY);
      return rejectWithValue('Authentication token expired');
    }

    // In a real app, this would be an API call with the token
    // const response = await fetch('/api/me', {
    //   headers: { Authorization: `Bearer ${auth.token}` },
    //   });
    // if (!response.ok) {
    //   const error = await ApiError.fromResponse(response);
    //   throw error;
    // }
    // const data = await response.json();
    // return data;

    // Mock implementation for demo
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Extract user info from token (in a real app, this would be decoded from JWT)
    const tokenParts = auth.token.split('-');
    if (tokenParts[0] !== 'mock' || tokenParts[1] !== 'jwt' || tokenParts[2] !== 'token') {
      localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
      localStorage.removeItem(AUTH_CONFIG.TOKEN_EXPIRY_KEY);
      return rejectWithValue('Invalid token');
    }

    // Find the user based on stored token
    // In a real app, you would decode the JWT to get the user ID
    const validUser = VALID_CREDENTIALS[0]; // Default to first user for demo
    
    return {
      id: '123',
      name: validUser.name,
      email: validUser.email,
      avatar: '/images/default-avatar.png',
      role: validUser.role
    };
  } catch (error) {
    let message = 'Failed to get user';
    
    if (error instanceof ApiError) {
      message = error.message;
      
      // Handle unauthorized errors by clearing tokens
      if (error.type === ApiErrorType.UNAUTHORIZED) {
        localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
        localStorage.removeItem(AUTH_CONFIG.TOKEN_EXPIRY_KEY);
      }
    } else if (error instanceof Error) {
      message = error.message;
    }
    
    return rejectWithValue(message);
  }
});

// Create the slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
    setToken: (state, action: PayloadAction<string | null>) => {
      state.token = action.payload;
      if (action.payload) {
        localStorage.setItem('token', action.payload);
      } else {
        localStorage.removeItem('token');
      }
      state.isAuthenticated = !!action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Handle logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      })
      
      // Handle getCurrentUser
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      });
  }
});

// Export actions
export const { setUser, setToken, setError } = authSlice.actions;

// Export selectors
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectToken = (state: { auth: AuthState }) => state.auth.token;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.loading;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;

export default authSlice.reducer;
